import React, { useState, useEffect, useRef } from 'react';
import { apiBaseUrl } from '../../../config';
import { useAuth } from '../../../common/Context/AuthContext';
import LoginPrompt from '../../loginPrompt/LoginPrompt';
import { getLocalStorage, TRY_MODE } from '../../../common/Utils/Utils';
import { useStoreValues } from '../../../common/Context/GlobalContext/GlobalStateProvider';
import CreditAlert from '../../creditAlert/CreditAlert';
import { useLoader } from '../../../common/Context/LoaderContext';
import { FEATURE_FLAGS } from '../../../common/config/featureFlags';
import { getDummyProductData } from '../../../common/config/dummyProductConfigs';
import RenderProductComponent from './renderProductComponent/RenderProductComponent';
const TryNow = ({ productId }) => {
  const { user } = useAuth();
  // const { productId } = useParams();
  const { creditData } = useStoreValues();
  const { setLoading } = useLoader();
  const [currentProduct, setCurrentProduct] = useState({});
  const [productConfig, setProductConfig] = useState(null);
  const scrollRef = useRef();
  const isCreditAvailable = creditData?.isCreditAvailable;

  const handleScrollToComponent = () => {
    scrollRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    const fetchProductInfo = async () => {
      setLoading(true);
      const token = getLocalStorage('token');
      try {
        const url = `${apiBaseUrl}/api/Product/get-sub-product?productId=${productId}`;
        console.log('Fetching product info from:', url);

        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        });
        console.log('Response details:', {
          status: response.status,
          url: response.url,
          ok: response.ok,
          statusText: response.statusText,
        });
        if (response.ok) {
          const product = await response.json();
          if (product) {
            console.log('Product info:', product);

            // Conditionally use dummy data or API data based on feature flag
            if (FEATURE_FLAGS.ENABLE_DYNAMIC_PRODUCT_CONFIG) {
              console.log('🔄 Dynamic Product Config: ENABLED');

              // Check if we should use dummy data (feature flag enabled)
              if (FEATURE_FLAGS.USE_DUMMY_RESPONSE) {
                console.log(
                  '📦 USE_DUMMY_RESPONSE flag enabled - Using COMPLETE DUMMY product data for demo'
                );

                // Use complete dummy data
                const dummyData = getDummyProductData(product.data.productKey);

                if (dummyData) {
                  console.log(
                    '✅ Dummy data loaded for:',
                    product.data.productKey
                  );
                  console.log(
                    '🔑 ClientKey: Using HARDCODED value from dummy config headers (demo mode)'
                  );
                  // Use dummy data - NO clientKey needed (it's hardcoded in headers with source='constant')
                  setCurrentProduct({
                    productId: dummyData.productId,
                    productName: dummyData.productName,
                    productKey: dummyData.productKey,
                    title: dummyData.productTitle,
                    productWeightage: dummyData.productWeightage,
                    pageWeightage: dummyData.pageWeightage,
                    description: dummyData.productSubTitle,
                    about: dummyData.productAbout,
                    tryThis:
                      dummyData.tryThis ||
                      `Test ${dummyData.productName} with sample data to see how it processes and extracts information in real-time.`,
                    documentation:
                      dummyData.documentation ||
                      'Complete API documentation including endpoints, request/response formats, and integration guides.',
                  });
                  setProductConfig(dummyData.productConfig);
                } else {
                  console.warn(
                    '⚠️ No dummy data found for:',
                    product.data.productKey
                  );
                  // Fallback to API response structure
                  setCurrentProduct({
                    clientKey: product.data.clientKey,
                    productId: product.data.productId,
                    productName: product.data.productName,
                    productKey: product.data.productKey,
                    title: product.data.productTitle,
                    productWeightage: product.data.productWeightage,
                    pageWeightage: product.data.pageWeightage,
                    description: product.data.productSubTitle,
                    about:
                      product.data.productAbout ||
                      `${product.data.productTitle} provides advanced data processing capabilities powered by AI technology.`,
                    tryThis:
                      product.data.tryThis ||
                      `Test ${product.data.productName} with sample data to see how it processes and extracts information in real-time.`,
                    documentation:
                      product.data.documentation ||
                      'Complete API documentation including endpoints, request/response formats, and integration guides.',
                  });
                  setProductConfig({
                    mode: 'single',
                    baseURL: product.data.attributes?.BaseURL || '',
                    endpoints: { singlePath: '/process' },
                    httpMethod: 'POST',
                    requestBodySchema: [],
                    headers: [],
                    uiConfig: {},
                  });
                }
              }
              // Check if backend has productConfig ready
              else if (product.data.productConfig) {
                console.log(
                  '✅ Backend is READY - Using product config from API response'
                );

                // Use data from API response
                setCurrentProduct({
                  clientKey: product.data.clientKey,
                  productId: product.data.productId,
                  productName: product.data.productName,
                  productKey: product.data.productKey,
                  title: product.data.productTitle,
                  productWeightage: product.data.productWeightage,
                  pageWeightage: product.data.pageWeightage,
                  description: product.data.productSubTitle,
                  about: product.data.productAbout,
                  tryThis: product.data.tryThis,
                  documentation: product.data.documentation,
                });
                setProductConfig(product.data.productConfig);
              } else {
                console.log(
                  '📦 Backend NOT ready - Using COMPLETE DUMMY product data for demo'
                );

                // Backend not ready - use complete dummy data
                const dummyData = getDummyProductData(product.data.productKey);

                if (dummyData) {
                  console.log(
                    '✅ Dummy data loaded for:',
                    product.data.productKey
                  );
                  console.log(
                    '🔑 ClientKey: Using HARDCODED value from dummy config headers (demo mode)'
                  );
                  // Use dummy data - NO clientKey needed (it's hardcoded in headers with source='constant')
                  setCurrentProduct({
                    productId: dummyData.productId,
                    productName: dummyData.productName,
                    productKey: dummyData.productKey,
                    title: dummyData.productTitle,
                    productWeightage: dummyData.productWeightage,
                    pageWeightage: dummyData.pageWeightage,
                    description: dummyData.productSubTitle,
                    about: dummyData.productAbout,
                    tryThis:
                      dummyData.tryThis ||
                      `Test ${dummyData.productName} with sample data to see how it processes and extracts information in real-time.`,
                    documentation:
                      dummyData.documentation ||
                      'Complete API documentation including endpoints, request/response formats, and integration guides.',
                  });
                  setProductConfig(dummyData.productConfig);
                } else {
                  console.warn(
                    '⚠️ No dummy data found for:',
                    product.data.productKey
                  );
                  // Fallback to API response structure
                  setCurrentProduct({
                    clientKey: product.data.clientKey,
                    productId: product.data.productId,
                    productName: product.data.productName,
                    productKey: product.data.productKey,
                    title: product.data.productTitle,
                    productWeightage: product.data.productWeightage,
                    pageWeightage: product.data.pageWeightage,
                    description: product.data.productSubTitle,
                    about:
                      product.data.productAbout ||
                      `${product.data.productTitle} provides advanced data processing capabilities powered by AI technology.`,
                    tryThis:
                      product.data.tryThis ||
                      `Test ${product.data.productName} with sample data to see how it processes and extracts information in real-time.`,
                    documentation:
                      product.data.documentation ||
                      'Complete API documentation including endpoints, request/response formats, and integration guides.',
                  });
                  setProductConfig({
                    mode: 'single',
                    baseURL: product.data.attributes?.BaseURL || '',
                    endpoints: { singlePath: '/process' },
                    httpMethod: 'POST',
                    requestBodySchema: [],
                    headers: [],
                    uiConfig: {},
                  });
                }
              }
            } else {
              console.log(
                '⏸️ Dynamic Product Config: DISABLED (using legacy hardcoded config)'
              );

              // Legacy mode - use API response as before
              setCurrentProduct({
                clientKey: product.data.clientKey,
                productId: product.data.productId,
                productName: product.data.productName,
                productKey: product.data.productKey,
                title: product.data.productTitle,
                productWeightage: product.data.productWeightage,
                pageWeightage: product.data.pageWeightage,
                description: product.data.productSubTitle,
                about:
                  product.data.productAbout ||
                  `${product.data.productTitle} provides advanced data processing capabilities powered by AI technology.`,
                tryThis:
                  product.data.tryThis ||
                  `Test ${product.data.productName} with sample data to see how it processes and extracts information in real-time.`,
                documentation:
                  product.data.documentation ||
                  'Complete API documentation including endpoints, request/response formats, and integration guides.',
                apiEndpoint: product.data.attributes?.BaseURL,
              });
              // Don't set productConfig, use legacy hardcoded configuration
              setProductConfig(null);
            }
          } else {
            console.warn('Product not found for productId:', productId);
          }
        } else {
          throw new Error(`API responded with status: ${response.status}`);
        }
      } catch (error) {
        console.error('Error fetching product info:', error);
      } finally {
        setLoading(false);
      }
    };

    if (productId) {
      fetchProductInfo();
    }
  }, [productId]);

  return (
    <div>
      <div className="">
        <div className="">
          {user ? (
            isCreditAvailable ? (
              <RenderProductComponent
                currentProduct={currentProduct}
                productConfig={productConfig}
              />
            ) : (
              <CreditAlert productKey={currentProduct.productKey} />
            )
          ) : (
            <LoginPrompt message="to try this feature." />
          )}
        </div>
      </div>
    </div>
  );
};

export default TryNow;
